import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, Check } from "lucide-react";

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const wsRef = useRef(null);
  const navigate = useNavigate();

  const load = () => {
    if (!user) return;
    api.get("/notifications").then(r => setNotifs(r.data));
  };

  useEffect(() => {
    if (!user) return;
    load();
    const token = localStorage.getItem("lima_token");
    if (!token) return;
    const wsBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/ws/${user.id}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);
        if (payload.type === "notification") {
          setNotifs(prev => [payload.data, ...prev]);
        }
      } catch {}
    };
    const ping = setInterval(() => { if (ws.readyState === 1) ws.send("ping"); }, 25000);
    return () => { clearInterval(ping); ws.close(); };
    // eslint-disable-next-line
  }, [user?.id]);

  if (!user) return null;
  const unread = notifs.filter(n => !n.read).length;

  const openNotif = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    setNotifs(prev => prev.map(x => x.id === n.id ? {...x, read: true} : x));
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setNotifs(prev => prev.map(x => ({...x, read: true})));
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} className="relative p-2 rounded-full hover:bg-black/5" data-testid="notifications-bell">
        <Bell size={18}/>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[#2E6B57] text-[#FFFFFF] text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center border-2 border-[#F2F4F2]" data-testid="notifications-unread-count">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)}/>
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-black/5 z-40 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
              <h3 className="font-bold">Notifications</h3>
              {notifs.length > 0 && <button onClick={markAll} className="text-xs text-[#245544] font-semibold" data-testid="notifications-read-all">Mark all read</button>}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {notifs.length === 0 && <p className="text-center text-sm text-[#222]/50 py-10">You're all caught up</p>}
              {notifs.map(n => (
                <button key={n.id} onClick={() => openNotif(n)} className={`w-full flex items-start gap-3 p-3 border-b border-black/5 text-left hover:bg-black/[0.02] ${!n.read ? "bg-[#2E6B57]/5" : ""}`} data-testid={`notification-${n.id}`}>
                  <div className={`w-2 h-2 mt-2 rounded-full shrink-0 ${!n.read ? "bg-[#2E6B57]" : "bg-transparent"}`}/>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{n.title}</p>
                    <p className="text-xs text-[#222]/60 line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-[#222]/40 mt-0.5">{new Date(n.created_at).toLocaleDateString()} {new Date(n.created_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
